import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Send, MessageSquare } from "lucide-react";

export default function EnquiryModal({ product, open, onOpenChange }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    travel_date: "",
    adults: 1,
    children: 0,
    pickup_city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Name and phone number are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        ...form,
        product_id: product?.id,
        product_title: product?.title,
        source: "product_page",
      });
      setSubmitted(true);
      toast.success("Enquiry sent! We'll contact you shortly.");
    } catch {
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (submitted) {
      setSubmitted(false);
      setForm({ full_name: "", email: "", phone: "", travel_date: "", adults: 1, children: 0, pickup_city: "", message: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enquiry-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            {submitted ? "Enquiry Sent!" : "Get a Quote"}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-6" data-testid="enquiry-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <Send className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="font-display text-lg mb-2">Thank you!</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Our travel expert will contact you within 30 minutes during business hours (9 AM - 8 PM IST).
            </p>
            {product && (
              <p className="text-xs text-muted-foreground">
                Enquiry for: <span className="font-medium text-foreground">{product.title}</span>
              </p>
            )}
            <Button onClick={handleClose} className="mt-4" data-testid="enquiry-close">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="enquiry-form">
            {product && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <span className="text-muted-foreground">Enquiry for: </span>
                <span className="font-medium">{product.title}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  placeholder="Your name"
                  data-testid="enquiry-name"
                  required
                />
              </div>
              <div>
                <Label>Mobile *</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 98765 43210"
                  data-testid="enquiry-phone"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@email.com"
                data-testid="enquiry-email"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Travel Date</Label>
                <Input
                  type="date"
                  value={form.travel_date}
                  onChange={(e) => set("travel_date", e.target.value)}
                  data-testid="enquiry-date"
                />
              </div>
              <div>
                <Label>Adults</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.adults}
                  onChange={(e) => set("adults", parseInt(e.target.value) || 1)}
                  data-testid="enquiry-adults"
                />
              </div>
              <div>
                <Label>Children</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.children}
                  onChange={(e) => set("children", parseInt(e.target.value) || 0)}
                  data-testid="enquiry-children"
                />
              </div>
            </div>

            <div>
              <Label>Pickup City</Label>
              <Input
                value={form.pickup_city}
                onChange={(e) => set("pickup_city", e.target.value)}
                placeholder="e.g. Hyderabad, Secunderabad"
                data-testid="enquiry-city"
              />
            </div>

            <div>
              <Label>Special Requirements</Label>
              <Textarea
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Any special requests, group size details, budget range..."
                rows={3}
                data-testid="enquiry-message"
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting} data-testid="enquiry-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {submitting ? "Submitting..." : "Send Enquiry"}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              By submitting, you agree to be contacted via WhatsApp, email, or phone regarding this enquiry.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
